import React, { ReactElement, useEffect, useRef, useState, useCallback, useMemo } from "react";
import { Button, Alert } from "react-bootstrap";
import pDebounce from "p-debounce";
import { useFormikContext } from "formik";
import { GetAdresEncoded } from "../../shared/components/mapsimage/MapImage";
import { WoningState } from "../infra/woning-schema";
import {
  useGoogleMapsAPI,
  initializeStreetView,
  initializeMap,
  createSafeStateSetter,
  updateStreetViewPosition
} from "../../shared/utils/google-streetview";

/* eslint-disable no-console */

//tod: 131051 , ISW KEY:
const GOOGLE_MAPS_API_KEY = "AIzaSyARq6ch8IF_El2dSJlocyZYJRUj2H_FwQA";

interface InteractiveStreetViewProps {
  onPositionChange: (streetViewUrl: string) => void;
  onSnapshot?: (streetViewUrl: string) => void;
  onAddressChanged?: () => void;
  addressChangeKey?: number;
  initialUrl?: string;
  className?: string;
  isHuidigeWoning?: boolean; // Make optional
}

const InteractiveStreetView = React.memo(
  ({
    onPositionChange,
    onSnapshot,
    onAddressChanged,
    addressChangeKey,
    initialUrl,
    className,
    isHuidigeWoning = false // Default value set here
  }: InteractiveStreetViewProps): ReactElement => {
    const { values } = useFormikContext<WoningState>();
    const huidigeWoningIndex = isHuidigeWoning
      ? values.huidigeWoningen?.findIndex(hw => hw.pandId === values.hypotheekOpWoning)
      : null;
    const address =
      huidigeWoningIndex !== null && huidigeWoningIndex !== undefined && values?.huidigeWoningen?.[huidigeWoningIndex]
        ? values.huidigeWoningen[huidigeWoningIndex]?.adres
        : values?.aanTeKopenWoning?.adres;
    const streetViewRef = useRef<HTMLDivElement>(null);
    const mapRef = useRef<HTMLDivElement>(null);
    const streetViewInstance = useRef<google.maps.StreetViewPanorama | null>(null);
    const mapInstance = useRef<google.maps.Map | null>(null);
    const markerInstance = useRef<google.maps.Marker | null>(null);
    const isMountedRef = useRef(true);
    const initializationInProgressRef = useRef(false);
    // const abortControllerRef = useRef<AbortController>(new AbortController());
    const [viewMode] = useState<"streetview" | "map">("streetview");
    const [currentStreetViewUrl, setCurrentStreetViewUrl] = useState<string | null>(null);
    const [isInitializing, setIsInitializing] = useState(false);
    const [initializationError, setInitializationError] = useState<string | null>(null);
    const [hasInitializedOnce, setHasInitializedOnce] = useState(false);
    const [showLoadingAfterDebounce, setShowLoadingAfterDebounce] = useState(false);
    const [previousAddressChangeKey, setPreviousAddressChangeKey] = useState<number | undefined>(addressChangeKey);
    const {
      isLoaded: apiLoaded,
      isLoading: apiLoading,
      error: apiError,
      generateStreetViewUrl,
      parseStreetViewUrl,
      geocodeAddress
    } = useGoogleMapsAPI();

    // Create a stable address string to track meaningful address changes
    const addressString = `${address.straat || ""}-${address.huisnummer || ""}-${address.postcode ||
      ""}-${address.plaats || ""}`;

    // Safe state update functions to prevent memory leaks
    const safeSetIsInitializing = useCallback(createSafeStateSetter(setIsInitializing, isMountedRef), []);
    const safeSetInitializationError = useCallback(createSafeStateSetter(setInitializationError, isMountedRef), []);
    const safeSetCurrentStreetViewUrl = useCallback(createSafeStateSetter(setCurrentStreetViewUrl, isMountedRef), []);
    const safeSetPreviousAddressChangeKey = useCallback(
      createSafeStateSetter(setPreviousAddressChangeKey, isMountedRef),
      []
    );
    const safeSetShowLoadingAfterDebounce = useCallback(
      createSafeStateSetter(setShowLoadingAfterDebounce, isMountedRef),
      []
    );
    const safeSetHasInitializedOnce = useCallback(createSafeStateSetter(setHasInitializedOnce, isMountedRef), []);

    // Initialize Street View using utility function
    const initializeStreetViewWrapper = useCallback(async (): Promise<void> => {
      await initializeStreetView({
        apiLoaded,
        streetViewRef,
        streetViewInstance,
        isInitializing,
        address,
        initializationInProgressRef,
        isMountedRef,
        initialUrl,
        hasInitializedOnce,
        isAddressChange: false, // No longer using address change flag for reinitialization
        parseStreetViewUrl,
        generateStreetViewUrl,
        geocodeAddress,
        onPositionChange,
        onAddressChanged,
        safeSetIsInitializing,
        safeSetInitializationError,
        safeSetCurrentStreetViewUrl,
        setShowLoadingAfterDebounce: safeSetShowLoadingAfterDebounce
      });
    }, [
      address,
      isInitializing,
      apiLoaded,
      initialUrl,
      parseStreetViewUrl,
      generateStreetViewUrl,
      geocodeAddress,
      onPositionChange,
      safeSetCurrentStreetViewUrl,
      safeSetInitializationError,
      safeSetIsInitializing,
      onAddressChanged,
      hasInitializedOnce,
      safeSetShowLoadingAfterDebounce
    ]);

    // Initialize Map using utility function
    const initializeMapWrapper = useCallback(async (): Promise<void> => {
      await initializeMap({
        apiLoaded,
        mapRef,
        mapInstance,
        markerInstance,
        isInitializing,
        address,
        isMountedRef,
        initialUrl,
        hasInitializedOnce,
        parseStreetViewUrl,
        generateStreetViewUrl,
        geocodeAddress,
        onPositionChange,
        safeSetCurrentStreetViewUrl
      });
    }, [
      address,
      apiLoaded,
      generateStreetViewUrl,
      geocodeAddress,
      onPositionChange,
      isInitializing,
      safeSetCurrentStreetViewUrl,
      initialUrl,
      parseStreetViewUrl,
      hasInitializedOnce
    ]);

    /* istanbul ignore next */
    const updateStreetViewLocation = useCallback(
      async (signal?: AbortSignal) => {
        if (!streetViewInstance.current || !apiLoaded || !geocodeAddress || !isMountedRef.current || signal?.aborted) {
          return;
        }

        try {
          const addressString = `${address.straat || ""} ${address.huisnummer || ""}, ${address.postcode ||
            ""} ${address.plaats || ""}`.trim();

          if (!address.postcode || !address.huisnummer) {
            console.log("Address is incomplete for location update");
            return;
          }

          // Check if operation was aborted before proceeding
          if (signal?.aborted) return;

          console.log("Updating street view location for address:", addressString);

          // Geocode the new address
          /* istanbul ignore next */
          const newPosition = await geocodeAddress(addressString);

          // Check if operation was aborted after async operation or component unmounted
          if (false || signal?.aborted || !isMountedRef.current) return;

          if (!newPosition) {
            console.log("Could not geocode new address");
            return;
          }

          // Use the utility function to update Street View position
          await updateStreetViewPosition(streetViewInstance.current, newPosition, () => {
            // Position updated (only if component is still mounted)
            //if (!isMountedRef.current) return;
          });

          // Check if operation was aborted after async operation or component unmounted
          //if (signal?.aborted || !isMountedRef.current) return;

          // Update map position if it exists (only if component is still mounted)
          if (isMountedRef.current && mapInstance.current && markerInstance.current) {
            (mapInstance.current as any).panTo(newPosition);
            markerInstance.current.setPosition(newPosition);
          }

          console.log("Street view location updated to:", newPosition);
        } catch (error) {
          console.log("Failed to update street view location:", error);
        }
      },
      [address.straat, address.huisnummer, address.postcode, address.plaats, apiLoaded, geocodeAddress]
    );

    // Effect to detect addressChangeKey changes and trigger lightweight street view update
    useEffect(() => {
      // Early return if component is unmounted
      if (!isMountedRef.current) {
        return;
      }

      /* istanbul ignore next */
      if (
        addressChangeKey !== undefined &&
        previousAddressChangeKey !== undefined &&
        addressChangeKey !== previousAddressChangeKey
      ) {
        console.log("Address change key changed from", previousAddressChangeKey, "to", addressChangeKey);

        // Update the previous key safely (only if mounted)
        if (isMountedRef.current) {
          safeSetPreviousAddressChangeKey(addressChangeKey);
        }

        updateStreetViewLocation().catch(/* istanbul ignore next */ () => {});
      } else if (addressChangeKey !== previousAddressChangeKey && isMountedRef.current) {
        // Update previous key even if onAddressChanged is not provided (only if mounted)
        safeSetPreviousAddressChangeKey(addressChangeKey);
      }
    }, [addressChangeKey, previousAddressChangeKey, updateStreetViewLocation, safeSetPreviousAddressChangeKey]);

    // Create debounced initialization function with AbortSignal
    const debouncedInitialization = useMemo(() => {
      const debounced = pDebounce(async (addressString: string, signal?: AbortSignal) => {
        // Check if operation was aborted
        if (signal?.aborted) return;

        // Double-check all conditions before initializing
        /* istanbul ignore else */
        if (
          isMountedRef.current &&
          !streetViewInstance.current &&
          !mapInstance.current &&
          !isInitializing &&
          apiLoaded &&
          streetViewRef.current &&
          mapRef.current &&
          address.postcode &&
          address.huisnummer
        ) {
          safeSetHasInitializedOnce(true);
          initializeStreetViewWrapper().catch(/* istanbul ignore next */ () => {});
          initializeMapWrapper().catch(/* istanbul ignore next */ () => {});
        }
      }, 1000);

      return debounced;
    }, [
      apiLoaded,
      isInitializing,
      initializeStreetViewWrapper,
      initializeMapWrapper,
      address.postcode,
      address.huisnummer,
      safeSetHasInitializedOnce
    ]);

    // Main initialization effect - only runs when API loads or when initialization is needed
    useEffect(() => {
      if (
        !apiLoaded ||
        !streetViewRef.current ||
        !mapRef.current ||
        streetViewInstance.current ||
        mapInstance.current ||
        isInitializing ||
        !address.postcode ||
        !address.huisnummer
      ) {
        return;
      }

      // Create a new AbortController for this initialization
      const controller = new AbortController();

      // Use pDebounce with AbortSignal for better async handling
      debouncedInitialization(addressString).catch(/* istanbul ignore next */ () => {});

      // Return cleanup function to abort the operation if the effect runs again
      return () => {
        controller.abort();
      };
    }, [apiLoaded, isInitializing, addressString, address.postcode, address.huisnummer, debouncedInitialization]);

    // Cleanup on unmount
    useEffect(() => {
      return () => {
        // Mark component as unmounted to prevent state updates
        isMountedRef.current = false;

        /* istanbul ignore next */
        if (typeof google !== "undefined" && google.maps?.event?.clearInstanceListeners) {
          if (streetViewInstance.current) {
            google.maps.event.clearInstanceListeners(streetViewInstance.current);
            streetViewInstance.current = null;
          }
          if (mapInstance.current) {
            google.maps.event.clearInstanceListeners(mapInstance.current);
            mapInstance.current = null;
          }
          if (markerInstance.current) {
            google.maps.event.clearInstanceListeners(markerInstance.current);
            markerInstance.current = null;
          }
        } else {
          // If google maps API is not available, just clear the references
          streetViewInstance.current = null;
          mapInstance.current = null;
          markerInstance.current = null;
        }
      };
    }, []);

    /* istanbul ignore next */
    const handleSnapshot = (): void => {
      if (!onSnapshot) return;

      // Generate fresh URL from current Street View state to capture latest zoom
      if (streetViewInstance.current && generateStreetViewUrl) {
        const currentPosition = streetViewInstance.current.getPosition();
        const pov = streetViewInstance.current.getPov();
        const zoom = streetViewInstance.current.getZoom();

        if (currentPosition) {
          const fov = Math.max(10, Math.min(120, 120 - (zoom || 1) * 22.5));

          const data = {
            location: { lat: currentPosition.lat(), lng: currentPosition.lng() },
            heading: pov.heading || 0,
            pitch: pov.pitch || 0,
            zoom: fov
          };

          const snapshotUrl = generateStreetViewUrl(data);
          onSnapshot(snapshotUrl);
        }
      } else if (currentStreetViewUrl) {
        // Fallback to state if Street View instance not available
        onSnapshot(currentStreetViewUrl);
      }
    };

    // Show static fallback if API failed to load
    if (apiError) {
      const addressString = GetAdresEncoded(address);
      const staticImageUrl = `https://maps.googleapis.com/maps/api/streetview?size=720x360&key=${GOOGLE_MAPS_API_KEY}&location=${addressString}`;

      return (
        <div className={className}>
          <Alert variant="info" className="mb-2">
            Using static Street View image ({apiError})
          </Alert>
          <img
            src={staticImageUrl}
            alt="Street View (static)"
            style={{
              width: "720px",
              height: "360px",
              objectFit: "cover",
              border: "1px solid #dee2e6",
              borderRadius: "4px",
              margin: "0 auto",
              display: "block"
            }}
            onLoad={() => {
              if (isMountedRef.current) {
                const urlWithoutProtocol = staticImageUrl.replace("https://", "");
                safeSetCurrentStreetViewUrl(urlWithoutProtocol);
                onPositionChange(urlWithoutProtocol);
              }
            }}
          />
        </div>
      );
    }

    return (
      <div className={className}>
        <div style={{ position: "relative" }}>
          {/* Street View Container */}
          <div
            ref={streetViewRef}
            style={{
              width: "720px",
              height: "360px",
              border: "1px solid #dee2e6",
              borderRadius: "4px",
              backgroundColor: "#f8f9fa",
              display: viewMode === "streetview" ? "block" : /* istanbul ignore next */ "none",
              margin: "0 auto"
            }}
          />

          {/* Map Container */}
          <div
            ref={mapRef}
            style={{
              width: "720px",
              height: "360px",
              border: "1px solid #dee2e6",
              borderRadius: "4px",
              backgroundColor: "#f8f9fa",
              display: viewMode === "map" ? "block" : "none",
              margin: "0 auto"
            }}
          />

          {/* Loading indicator while API is loading or components initialize */}
          {(apiLoading ||
            !apiLoaded ||
            isInitializing ||
            (showLoadingAfterDebounce &&
              !streetViewInstance.current &&
              !initializationError &&
              viewMode === "streetview") ||
            (!mapInstance.current && viewMode === "map")) && (
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                background: "rgba(255,255,255,0.9)",
                padding: "20px",
                borderRadius: "8px",
                textAlign: "center",
                zIndex: 1000
              }}
            >
              <div className="spinner-border spinner-border-sm mb-2" role="status">
                <span className="sr-only">Loading...</span>
              </div>
              <div>
                <small className="text-muted">
                  {apiLoading || !apiLoaded
                    ? "Loading Google Maps API..."
                    : viewMode === "streetview"
                    ? "Loading Street View..."
                    : /* istanbul ignore next */
                      "Loading Map..."}
                </small>
                {(apiLoading || !apiLoaded) && (
                  <>
                    <br />
                    <small className="text-muted">This may take a moment...</small>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Error indicator when Street View fails to load */}
          {/* istanbul ignore next */

          initializationError && viewMode === "streetview" && !streetViewInstance.current && (
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                background: "rgba(255,255,255,0.95)",
                padding: "20px",
                borderRadius: "8px",
                textAlign: "center",
                zIndex: 1000,
                border: "1px solid #f5c6cb",
                backgroundColor: "#f8d7da"
              }}
            >
              <div style={{ color: "#721c24", marginBottom: "8px" }}>
                <span role="img" aria-label="warning">
                  ⚠️
                </span>
              </div>
              <div>
                <small style={{ color: "#721c24" }}>{initializationError}</small>
              </div>
            </div>
          )}
        </div>

        {/* Snapshot Button - positioned under the streetview */}
        {onSnapshot && (
          <div className="d-flex justify-content-center mt-3">
            <Button
              variant="primary"
              size="sm"
              onClick={handleSnapshot}
              /* istanbul ignore next */
              disabled={!currentStreetViewUrl || (!streetViewInstance.current && !mapInstance.current)}
              aria-label="afbeelding vastleggen"
              className="d-flex align-items-center gap-2"
              style={{
                borderRadius: "8px",
                padding: "8px 16px",
                fontWeight: "500",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
              }}
            >
              <span role="img" aria-label="camera" style={{ fontSize: "1.2rem", marginTop: "-10px" }}>
                📷
              </span>{" "}
              <span>Afbeelding vastleggen</span>
            </Button>
          </div>
        )}
      </div>
    );
  }
);

InteractiveStreetView.displayName = "InteractiveStreetView";

export default InteractiveStreetView;
